import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
  defaultKeyboardCoordinateGetter
} from '@dnd-kit/core';
import KanbanColumn from './KanbanColumn';
import { useKanban } from '../../hooks/useKanban';
import FilterDropdown from '../../../../components/common/FilterDropdown';
import SearchBar from '../../../../components/common/SearchBar';

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

const KanbanView = ({ initialItems, refreshData }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadRef = React.useRef(null);
  React.useEffect(() => {
    const handleDocClick = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) setDownloadOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setDownloadOpen(false); };
    document.addEventListener('click', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);
  const [filterData, setFilterData] = useState({
    dateFrom: '',
    dateTo: '',
    assignee: '',
    stage: '',
    sector: '',
    source: ''
  });

  // Keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl/Cmd + K to focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
      
      // Escape to clear search
      if (event.key === 'Escape' && searchQuery) {
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery]);

  const handleFilterChange = (field, value) => {
    setFilterData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      assignee: '',
      stage: '',
      sector: '',
      source: ''
    });
  };

  const handleFilterApply = () => {
    // TODO: Implement filter logic
    // console.log('Applying filters:', filterData);
  };

  const {
    items,
    allItems,
    activeId,
    overStage,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragCancel,
    kanbanData,
    searchQuery: currentSearchQuery,
    isRefreshing,
    showSuccess,
    isUpdating
  } = useKanban(initialItems, searchQuery, refreshData);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: defaultKeyboardCoordinateGetter
    })
  );

  const draggedItem = activeId ? items.find(item => item.id === activeId) : null;

  // Calculate search statistics
  const totalItems = allItems.length;
  const filteredItemsCount = items.length;
  const isSearching = searchQuery.trim() !== '';

  // console.log('kanbanData:', kanbanData);

  Object.entries(kanbanData).forEach(([stage, data]) => {
    // console.log(`Stage: ${stage}, Items count:`, data.items.length, data.items);
  });

  return (
    <div>
      <div className="row align-items-center w-full mb-3">
        <div className="col">
          <FilterDropdown 
            filterData={filterData}
            onFilterChange={handleFilterChange}
            onReset={handleFilterReset}
            onApply={handleFilterApply}
            uniqueId="leads-kanban"
            filterFields={[
              {
                name: 'assignee',
                label: 'Assignee',
                searchField: 'name,email'
              },
              {
                name: 'stage',
                label: 'Stage'
              },
              {
                name: 'sector',
                label: 'Sector'
              },
              {
                name: 'source',
                label: 'Source'
              }
            ]}
            options={{
              assigneeOptions: [],
              stageOptions: [],
              sectorOptions: [],
              sourceOptions: []
            }}
          />
        </div>
        <div className="col-md-auto col-sm-12">
          <div className="ms-auto d-flex flex-wrap btn-list">
            <div className="position-relative">
              <SearchBar 
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search leads... (Ctrl+K)"
                uniqueId="leads-kanban"
              />
              {searchQuery && (
                <button 
                  className="btn btn-sm position-absolute"
                  style={{ 
                    right: '8px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    padding: '2px 6px',
                    fontSize: '12px'
                  }}
                  onClick={() => setSearchQuery('')}
                  title="Clear search (Esc)"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="dropdown" ref={downloadRef}>
              <a
                href="#"
                className="btn btn-outline-primary dropdown-toggle"
                aria-expanded={downloadOpen}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDownloadOpen(v => !v); }}
              >
                Download
              </a>
              <div className={`dropdown-menu dropdown-menu-end${downloadOpen ? ' show' : ''}`} style={{ right: 0, left: 'auto' }}>
                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); setDownloadOpen(false); }}>Export to Excel</a>
                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); setDownloadOpen(false); }}>Export to PDF</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Results Summary */}
      {isSearching && (
        <div className="alert alert-info mb-3" style={{ fontSize: '14px', padding: '12px 16px' }}>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <strong>🔍 Search Results:</strong> Found {filteredItemsCount} of {totalItems} leads
              {filteredItemsCount === 0 && (
                <span className="ms-2 text-muted">No leads match your search criteria</span>
              )}
              {filteredItemsCount > 0 && (
                <span className="ms-2 text-muted">
                  Searching in: Project Name, Company, PIC, Email, Description, Sector, Source, Stage
                </span>
              )}
            </div>
            <div className="d-flex gap-2">
              <small className="text-muted align-self-center">
                Press <kbd>Esc</kbd> to clear
              </small>
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setSearchQuery('')}
              >
                Clear Search
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="alert alert-success mb-3" style={{ fontSize: '14px', padding: '12px 16px' }}>
          <div className="d-flex align-items-center">
            <span>✅ Stage updated successfully! Refreshing data...</span>
          </div>
        </div>
      )}

      {/* Updating Message */}
      {isUpdating && !isRefreshing && (
        <div className="alert alert-info mb-3" style={{ fontSize: '14px', padding: '12px 16px' }}>
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Updating...</span>
            </div>
            <span>Updating stage...</span>
          </div>
        </div>
      )}

      {isRefreshing ? (
        // Show loading spinner in place of kanban content
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 150px)' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Refreshing...</span>
          </div>
          <span className="ms-2">Refreshing kanban data...</span>
        </div>
      ) : (
        // Show kanban content when not refreshing
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragCancel={handleDragCancel}
        >
          <div className="kanban-container" style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '16px',
            margin: '0 -12px'
          }}>
            <div className="kanban-board" style={{
              display: 'flex',
              gap: '24px',
              padding: '0 12px',
              minWidth: 'fit-content'
            }}>
              {Object.entries(kanbanData).map(([stage, data]) => (
                <KanbanColumn
                  key={stage}
                  stage={stage}
                  items={data.items}
                  isOver={overStage === stage}
                  isSearching={isSearching}
                  searchQuery={searchQuery}
                  isUpdating={isUpdating}
                />
              ))}
            </div>
          </div>
          <DragOverlay dropAnimation={dropAnimation}>
            {draggedItem ? (
              <div
                className="card card-sm mb-3"
                style={{
                  transform: 'rotate(3deg)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
                  cursor: 'grabbing',
                }}
              >
                <div className="card-body">
                  <h3 className="card-title">{draggedItem.companyName}</h3>
                  <div className="text-secondary mb-3">{draggedItem.description}</div>
                  <div className="badges-list mb-3">
                    {draggedItem.certifications && draggedItem.certifications.map((cert, index) => (
                      <span key={index} className={`badge bg-${['default', 'blue', 'azure', 'indigo'][index % 4]} text-${['default', 'blue', 'azure', 'indigo'][index % 4]}-fg`}>
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default KanbanView; 